import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import type AbilitiesService from 'ember-can/services/abilities';
import type Store from 'ember-data/store';
import type GlobalFlow from 'garaje/models/global-flow';
import type Questionnaire from 'garaje/models/questionnaire';
import type { GlobalOverviewQuestionnairesRouteModel } from 'garaje/pods/location-overview/questionnaires/route';
import type StateService from 'garaje/services/state';
import { SignInFieldKind } from 'garaje/utils/enums';
import { TrackedSet } from 'tracked-built-ins';

export interface GlobalOverviewQuestionnairesCreateRouteModel {
  changes: Set<Model>;
  fetchFlowsPage: GlobalOverviewQuestionnairesRouteModel['fetchFlowsPage'];
  flows: GlobalFlow[];
  questionnaire: Questionnaire;
}

export default class GlobalOverviewQuestionnairesCreateRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;

  redirect(): void {
    if (this.abilities.cannot('create global questionnaires')) {
      void this.router.replaceWith('location-overview.questionnaires');
    }
  }

  model(): GlobalOverviewQuestionnairesCreateRouteModel {
    const { fetchFlowsPage, flows } = <GlobalOverviewQuestionnairesRouteModel>(
      this.modelFor('location-overview.questionnaires')
    );

    const { changes, questionnaire } = this.#createEmptyQuestionnaire();
    return { changes, fetchFlowsPage, flows, questionnaire };
  }

  #createEmptyQuestionnaire(): { changes: Set<Model>; questionnaire: Questionnaire } {
    const changes = new TrackedSet<Model>();

    // Initialize a new questionnaire with a single (empty) section
    const questionnaire = this.store.createRecord('questionnaire', {
      title: 'New assessment',
      company: this.state.currentCompany,
    });
    changes.add(questionnaire);

    const section = this.store.createRecord('questionnaire-section', {
      position: 1,
      questionnaire,
    });
    changes.add(section);

    const question = this.store.createRecord('questionnaire-field', {
      questionnaireSection: section,
      kind: SignInFieldKind.SINGLE_SELECTION,
      position: 1,
    });
    changes.add(question);

    changes.add(
      this.store.createRecord('questionnaire-field-option', { questionnaireField: question, value: '', position: 1 }),
    );
    changes.add(
      this.store.createRecord('questionnaire-field-option', { questionnaireField: question, value: '', position: 2 }),
    );

    return { changes, questionnaire };
  }
}
